import Editor from '@monaco-editor/react';
import { T } from 'services/lang/LanguageService';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { Suspense, useEffect, useState } from 'react';
import { getDashboardComponentTypes } from 'components/ComponentType';
import { ComponentTypeWithID } from 'components/ComponentTypeTypes';
import { isJSON } from "utils/utils";

import 'style/overrides.scss';
import useComponentWidthStore from 'store/component-width-store';
import { easasAlert } from './easas-global-fragment/EasasGlobalAlerts';


type ConfigurationModel = {
    title: string;
    width: number | null;
    componentType: string | undefined;
    jsonConfig: string | undefined;
};

type ConfiguratorProps = {
    widgetVar: string;
    model: ConfigurationModel;
};

const DashboardComponentConfigurator: React.FC<ConfiguratorProps> = (props) => {
    const componentTypes: ComponentTypeWithID[] = getDashboardComponentTypes();
    const setComponentWidth = useComponentWidthStore(state => state.setComponentWidth);
    const jsonInputEl = document.getElementById(PF(props.widgetVar).id) as HTMLInputElement;
    const [configuratorState, setConfiguratorState] = useState<ConfigurationModel>({
        title: props.model.title,
        width: props.model.width,
        componentType: props.model.componentType,
        jsonConfig: props.model.jsonConfig,
    });
    
    useEffect(() => {
        jsonInputEl.value = JSON.stringify(configuratorState);
    }, [configuratorState]);

    const onJSONChange = (json: object) => {
        setConfiguratorState((prevState) => ({...prevState, jsonConfig: JSON.stringify(json)}));
    };

    const currentComponentType = componentTypes.find((element) => element.id === configuratorState.componentType);
    const SelectedComponent = currentComponentType?.editorEntryPoint;

    const componentSelectorHandler = (componentType: string) => {
        const json = configuratorState.jsonConfig?.length !== undefined && configuratorState.jsonConfig?.length > 2 ? configuratorState.jsonConfig : '{}'
        setConfiguratorState((prevState) => ({
            ...prevState,
            componentType,
            jsonConfig: json
        }))
    }

    const [activeIndex, setActiveIndex] = useState(0);

    const formatJSON = () => {
        try {
            const parsed = JSON.parse(configuratorState.jsonConfig as string);
            const formatted = JSON.stringify(parsed, null, 4);
            setConfiguratorState((prevState) => ({...prevState, jsonConfig: formatted}));
        } catch (error) {
            easasAlert({
              summary: T("alert.json-is-not-valid"),
              detail: (error as Error).message,
              severity: "error",
            });
        }
    };

    useEffect(() => {
        if (activeIndex === 1) {
            formatJSON();
        }
    }, [activeIndex]);

    const handleComponentWidthChange = (e: InputNumberValueChangeEvent) => {
      setConfiguratorState(prev => ({ ...prev, width: e.value! }));
      setComponentWidth(e.value);
    };

    return (
        <div style={{width: '100%', padding: '0 1rem 0 1rem', position: "relative"}}>
            <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col">
                    <label htmlFor="title">{T("word.title")}</label>
                    <InputText id="title" type="text" value={configuratorState.title}
                               onChange={(e) => setConfiguratorState((prevState) => ({
                                   ...prevState,
                                   title: e.target.value
                               }))}/>
                </div>
            </div>

            <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col">
                    <label htmlFor="type">{T("label.component-type")}</label>
                    <Dropdown
                        value={configuratorState.componentType}
                        options={componentTypes}
                        filter
                        onChange={(e) => componentSelectorHandler(e.target.value)}
                        optionLabel="name"
                        optionValue="id"
                        placeholder={T("action.select-a-component-type")}
                    />
                </div>
                <div className="p-field p-col">
                    <label htmlFor="width">{T("word.width")}</label>
                    <InputNumber
                        inputId="width"
                        min={2}
                        max={12}
                        value={configuratorState.width}
                        onValueChange={handleComponentWidthChange}
                    />
                </div>
            </div>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                {SelectedComponent && isJSON(configuratorState.jsonConfig) && (
                    <TabPanel header={T("label.editor")}>
                        <Suspense fallback={<ProgressSpinner/>}>
                            <SelectedComponent config={JSON.parse(configuratorState.jsonConfig as string)}
                                               onConfigChange={onJSONChange}/>
                        </Suspense>
                    </TabPanel>
                )}
                <TabPanel header={T("label.edit-raw-json")}>
                    <div className="p-fluid p-formgrid p-grid">
                        <div className="p-field p-col">
                            <Editor
                                height="420px"
                                defaultLanguage="json"
                                value={configuratorState.jsonConfig}
                                onChange={(e) => setConfiguratorState((prevState) => ({...prevState, jsonConfig: e}))}
                                onValidate={(markers) => {
                                    markers[0] &&
                                      easasAlert({
                                        summary: T("alert.json-is-not-valid"),
                                        detail: markers[0].message,
                                        severity: "error",
                                      });
                                }}
                                theme="vs-dark"
                            />
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </div>
    );
};

export default DashboardComponentConfigurator;
